/**
 * Excel export service for exporting bottles and distilleries
 */

import ExcelJS from 'exceljs';
import { supabase } from './supabase';

/**
 * Fetches all bottles with distillery information for export
 * @returns {Promise<Array>} All bottles with distillery data
 */
async function fetchAllBottles() {
  const { data, error } = await supabase
    .from('bottles')
    .select('*, distilleries(distillery_name)')
    .order('purchase_date', { ascending: true, nullsFirst: true })
    .order('bottle_name', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Fetches all distilleries for export
 * @returns {Promise<Array>} All distilleries
 */
async function fetchAllDistilleries() {
  const { data, error } = await supabase
    .from('distilleries')
    .select('*')
    .order('distillery_name', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Transforms bottle data for Excel export
 * @param {Array} bottles - Raw bottle data from database
 * @returns {Array} Transformed bottle data for export
 */
function transformBottlesForExport(bottles) {
  return bottles.map(bottle => [
    bottle.bottle_name,
    bottle.distilleries?.distillery_name || '',
    bottle.type,
    bottle.status,
    bottle.size,
    bottle.proof,
    bottle.age_months,
    bottle.purchase_date ? new Date(bottle.purchase_date + 'T00:00:00') : null,
    bottle.purchase_price ?? null,
    bottle.single_barrel ? 'Yes' : 'No',
    bottle.bottled_in_bond ? 'Yes' : 'No',
    bottle.cask_strength ? 'Yes' : 'No',
    bottle.small_batch ? 'Yes' : 'No',
    bottle.double_barreled ? 'Yes' : 'No',
    bottle.store_pick || '',
    bottle.finish_cask || '',
    bottle.notes || '',
    bottle.tasting_notes || ''
  ]);
}

/**
 * Transforms distillery data for Excel export
 * @param {Array} distilleries - Raw distillery data from database
 * @returns {Array} Transformed distillery data for export
 */
function transformDistilleriesForExport(distilleries) {
  return distilleries.map(distillery => [
    distillery.distillery_name,
    distillery.country,
    distillery.state_region || '',
    distillery.city || '',
    distillery.address || '',
    distillery.year_founded || ''
  ]);
}

/**
 * Exports bottles and distilleries to a single Excel file
 * @returns {Promise<void>}
 */
export async function exportToExcel() {
  // Fetch all data
  const [bottles, distilleries] = await Promise.all([
    fetchAllBottles(),
    fetchAllDistilleries()
  ]);

  // Transform data for export
  const bottlesData = transformBottlesForExport(bottles);
  const distilleriesData = transformDistilleriesForExport(distilleries);

  // Create workbook
  const workbook = new ExcelJS.Workbook();

  // Create Bottles worksheet
  const bottlesSheet = workbook.addWorksheet('Bottles', {
    views: [{ state: 'frozen', ySplit: 1 }]
  });

  // Add headers
  const bottleHeaders = [
    'Bottle Name', 'Distillery', 'Type', 'Status', 'Size', 'Proof',
    'Age (Months)', 'Purchase Date', 'Purchase Price', 'Single Barrel',
    'Bottled in Bond', 'Cask Strength', 'Small Batch', 'Double Barreled',
    'Store Pick', 'Finish Cask', 'Notes', 'Tasting Notes'
  ];
  bottlesSheet.addRow(bottleHeaders);

  // Add data rows
  bottlesData.forEach(row => bottlesSheet.addRow(row));

  // Set column widths
  bottlesSheet.columns = [
    { width: 30 }, // Bottle Name
    { width: 25 }, // Distillery
    { width: 15 }, // Type
    { width: 12 }, // Status
    { width: 10 }, // Size
    { width: 10 }, // Proof
    { width: 12 }, // Age
    { width: 12 }, // Purchase Date
    { width: 14 }, // Purchase Price
    { width: 12 }, // Single Barrel
    { width: 14 }, // Bottled in Bond
    { width: 13 }, // Cask Strength
    { width: 12 }, // Small Batch
    { width: 14 }, // Double Barreled
    { width: 20 }, // Store Pick
    { width: 20 }, // Finish Cask
    { width: 40 }, // Notes
    { width: 40 }  // Tasting Notes
  ];

  // Apply date format to Purchase Date column (H) and currency format to Purchase Price column (I)
  const purchaseDateCol = bottlesSheet.getColumn(8);
  purchaseDateCol.numFmt = 'mm/dd/yyyy';

  const purchasePriceCol = bottlesSheet.getColumn(9);
  purchasePriceCol.numFmt = '"$"#,##0.00';

  // Apply auto-filter
  bottlesSheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: bottlesData.length + 1, column: 18 }
  };

  // Create Distilleries worksheet
  const distilleriesSheet = workbook.addWorksheet('Distilleries', {
    views: [{ state: 'frozen', ySplit: 1 }]
  });

  // Add headers
  const distilleryHeaders = [
    'Distillery Name', 'Country', 'State/Region', 'City', 'Address', 'Year Founded'
  ];
  distilleriesSheet.addRow(distilleryHeaders);

  // Add data rows
  distilleriesData.forEach(row => distilleriesSheet.addRow(row));

  // Set column widths
  distilleriesSheet.columns = [
    { width: 30 }, // Distillery Name
    { width: 15 }, // Country
    { width: 20 }, // State/Region
    { width: 20 }, // City
    { width: 40 }, // Address
    { width: 12 }  // Year Founded
  ];

  // Apply auto-filter
  distilleriesSheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: distilleriesData.length + 1, column: 6 }
  };

  // Generate filename with date
  const date = new Date().toISOString().split('T')[0];
  const filename = `WhiskeyCollection_${date}.xlsx`;

  // Write and download file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export const excelExportService = {
  exportToExcel
};
